import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';

import beSendVoucherEmail from 'actions/client/beSendVoucherEmail.action';
import saveClient from 'actions/client/saveClient.action';
import fetchSalon from 'actions/fetchSalon.action';
import { feSidebarClose, feVoucherPanelToggle } from 'actions/ui.actions';
import filterActiveStylistsWithColumn from 'bl/data/filterActiveStylistsWithColumn.bl';
import { LT_SCHEDULER_DAY } from 'bl/loggers.bl';
import validateEmail from 'bl/validation/validateEmail.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import CustomDatePicker from 'components/form/CustomDatePicker.component';
import DatePickerInput from 'components/form/DatePickerInput.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import { USE_LOGGER_SCHEDAY } from 'config/use.conf';
import { HELP_LINKS } from 'constants/helpCenterLinks.const';
import LINK from 'constants/link.const';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import { useGilt, withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalon, selectCurrentSalonCountryCode } from 'selectors/currentSalon.selectors';
import { selectVoucherPanelOpen } from 'selectors/ui.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyDateToZulu } from 'utils/converters/time/from.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatYearToDayCompact } from 'utils/date/format.util';
import { momentToSalonCalendarDate, salonCalendarDateToMoment } from 'utils/date/momentSalonCalendarDate.util';
import { nowAsMoment } from 'utils/date/now.util';
import { formatSchedulerWeekUrlSegment, getSchedulerWeekEndSunday, getSchedulerWeekStartMonday } from 'utils/date/schedulerWeekBound.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';

import { clearCreatedVoucher } from 'cashup/actions';
import { setCheckoutInitDataFromNav } from 'checkout/actions';
import PadLock from 'lib/components/PadLock';
import { createIfAllowed, setBookedByButton } from 'scheduler/actions';
import { ClientSearch } from 'scheduler/components/ClientSearch';
import ButtonMobileStaff from 'scheduler/components/common/ButtonMobileStaff.component';
import CogButton from 'scheduler/components/common/CogButton.component';
import { SmsBuyButton } from 'scheduler/components/common/SmsBuyButton.component';
import StylistDropdown from 'scheduler/components/common/StylistDropdown';
import ActivityFeedButton from 'scheduler/components/Header/ActivityFeedButton.component';
import GlossaryIcons from 'scheduler/components/Header/GlossaryIcons.component';
import MarketingTextHeader from 'scheduler/components/Header/MarketingTextHeader';
import SalonNameComponent from 'scheduler/components/Header/SalonName.component';
import SchedulerHeaderGatedAddAppointmentItem from 'scheduler/components/Header/SchedulerHeaderGatedAddAppointmentItem.component';
import { WORKAROUND_ID } from 'scheduler/components/Scheduler';
import VoucherView from 'scheduler/components/VoucherView';
import { DAY_VIEW, WEEK_VIEW } from 'scheduler/constants';
import { trackEvent } from 'tracking/actions';

import CN from './SchedulerHeader.module.scss';

const L = logger('SchedulerHeader');
const L_DAY = logger(LT_SCHEDULER_DAY);

const USE_NAV_ICONS = false;
const USE_NAV_MARKETING_TEXT = false;

const applyIntercomBannerCss = () => {
  // NOTE: the intercom banner is an iframe, it comes from outside the React root
  // apply CSS only when iframe banner (not iframe chat)

  const INTERCOM_SELECTOR_BANNER = 'iframe[name="intercom-banner-frame"]';
  const INTERCOM_SELECTOR_BUTTONS = '.scheduler-container';
  const INTERCOM_CLASS_BUTTONS = 'hasIntercomBanner';

  const intercomBannerFrame = document.querySelector(INTERCOM_SELECTOR_BANNER);
  const headerButtons = document.querySelector(INTERCOM_SELECTOR_BUTTONS);

  if (!intercomBannerFrame) return;

  if (intercomBannerFrame && headerButtons) {
    headerButtons.classList.add(INTERCOM_CLASS_BUTTONS);
  } else {
    headerButtons.classList.remove(INTERCOM_CLASS_BUTTONS);
  }
};

const WeekViewIntervalDisplay = ({ bgn, end }) => {
  const { formatDate } = useGilt();

  const bgnDayMonth = formatDate(bgn, { hideTime: true, hideWeekday: true, hideYear: true });
  const endDayMonth = formatDate(end, { hideTime: true, hideWeekday: true, hideYear: true });

  const endYear = new Date(end).getFullYear();

  const isSameMonthAndDay = bgnDayMonth === endDayMonth;

  return (
    <div data-bem="WeekViewIntervalDisplay" className={CN.weekViewIntervalDisplay}>
      {isSameMonthAndDay ? `${bgnDayMonth}` : `${bgnDayMonth} - ${endDayMonth}`}
      <ResponsiveBox.Desktop>
        <div className={CN.weekViewYear}>{endYear}</div>
      </ResponsiveBox.Desktop>
    </div>
  );
};

const DayViewDisplayDate = ({ selectedDate, onPickerOpen }) => {
  const { formatDate } = useGilt();

  return (
    <div
      onKeyDown={alwaysNoop}
      tabIndex="0"
      className={cn({
        [CN.pickerButtonText]: true,
        [CN.pickerButtonTextBig]: true,
      })}
      style={{ cursor: 'pointer' }}
      role="button"
      onClick={onPickerOpen}
    >
      <span className="selected-date">{formatDate(selectedDate, { hideTime: true })}</span>
    </div>
  );
};

class SchedulerHeader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      calendarModal: false,
      searchContainer: true,
      searchIsOpen: false,
      dropdown: false,
      isNewDropdownOpen: false,
      modalVoucher: false,
      modalVoucherFailed: false,
      modalConfirmSendEmail: false,
      email: '',
      originalEmail: '',
      voucher: null,
      recipient: null,
      errorMessage: '',
      windowWidth: window.innerWidth,
    };

    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    applyIntercomBannerCss();
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { voucherCreated } = nextProps;

    if (voucherCreated && voucherCreated !== 'failed') {
      this.setState({ voucher: voucherCreated });
      if (voucherCreated.purchased_for_clientId) {
        const recipient = this.props.currentSalon.clientList.find((e) => e.id.toString() === voucherCreated.purchased_for_clientId.toString());

        if (!this.state.recipient) {
          this.setState({ recipient });
        }
        if (recipient.email) {
          this.setState({ email: recipient.email, originalEmail: recipient.email });
        }
      }
    } else if (voucherCreated && voucherCreated === 'failed') {
      this.setState({ modalVoucherFailed: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({ isNewDropdownOpen: false });
    }
  };

  onShowAllStylist = () => {
    switch (this.props.selectedView) {
      case WEEK_VIEW: {
        this.props.onShowWeekView();
        break;
      }
      case DAY_VIEW:
        this.props.showAllStylist(this.props.stylists);
        break;
      default:
        break;
    }
  };

  onSelectStylist = (stylist) => {
    switch (this.props.selectedView) {
      case WEEK_VIEW: {
        this.props.onSelectStylist(stylist);
        break;
      }
      case DAY_VIEW:
        this.props.onShowDayView(stylist.id);
        break;
      default:
        break;
    }
  };

  toggleNewDropdown = () => void this.setState({ isNewDropdownOpen: !this.state.isNewDropdownOpen });

  setSearchOpen = (open) => {
    if (!document.querySelector('.booking-steps-from-checkout') && !document.querySelector('.BookingWindow.animated')) {
      this.setState({ searchIsOpen: open, dropdown: false });
    }
  };

  showDropdown = () => {
    if (!document.querySelector('.booking-steps-from-checkout') && !document.querySelector('.BookingWindow.animated')) {
      this.setState({ dropdown: !this.state.dropdown });
    }
  };

  handleSchedulerDayPicked = (pickedDate) => {
    const tz = this.props.currentSalon?.timezone;
    this.props.dayPick(salonCalendarDateToMoment(pickedDate, tz));
    this.setState({ calendarModal: false });
  };

  handleSchedulerWeekPicked = (pickedDate) => {
    const tz = this.props.currentSalon?.timezone;
    this.props.onChangeDate(salonCalendarDateToMoment(pickedDate, tz));
    this.setState({ calendarModal: false });
  };

  calendarPrevClicked = () => {
    if (this.props.waiting_procedures || this.props.waiting_events) {
      return;
    }
    this.props.trackEvent('previous_day');
    switch (this.props.selectedView) {
      case WEEK_VIEW: {
        this.props.onChangeDate(moment(this.props.selectedDate).subtract(1, 'week'));
        break;
      }
      case DAY_VIEW:
        this.props.dayPick(moment(this.props.selectedDate).subtract(1, 'day'));
        break;
      default:
        break;
    }
  };

  calendarNextClicked = () => {
    const { onChangeDate, trackEvent, selectedDate, selectedView, dayPick, waiting_events: isWaitingEvents, waiting_procedures: isWaitingProcedures } = this.props;

    if (isWaitingProcedures || isWaitingEvents) return;

    trackEvent('next_day');

    if (WEEK_VIEW === selectedView) {
      onChangeDate(moment(selectedDate).add(1, 'week'));
    } else if (DAY_VIEW === selectedView) {
      dayPick(moment(selectedDate).add(1, 'day'));
    }
  };

  emailVoucherPurchaser = () => {
    this.props.feVoucherPanelToggle();
    L.unignore('emailVoucherPurchaser()', 'sendEmail:', this.sendEmail());
  };

  weekPick = (date) => {
    switch (this.props.selectedView) {
      case WEEK_VIEW: {
        this.props.onChangeDate(getSchedulerWeekStartMonday(moment(date)));
        break;
      }
      case DAY_VIEW:
        this.props.dayPick(date);
        break;
      default:
        break;
    }
    this.setState({ calendarModal: false });
  };

  hideInactiveHandler = () => {
    switch (this.props.selectedView) {
      case WEEK_VIEW: {
        this.props.onShowWeekView();
        break;
      }
      case DAY_VIEW:
        this.props.hideInactiveStylists(this.props.salonId, this.props.selectedDate);
        break;
      default:
        break;
    }
  };

  sendEmail = async () => {
    const { email, voucher } = this.state;

    if (validateEmail({ value: email }) && voucher.id) {
      try {
        const response = await this.props.beSendVoucherEmail({ voucherId: voucher.id, email });

        if (response.is.ok) {
          this.setState({ modalConfirmSendEmail: true });
          this.props.clearCreatedVoucher();
        } else {
          this.setState({ errorMessage: this.props.t('scheduler.emailValidationError') });
        }
      } catch (e) {
        L.warn('sendEmail()', e);
      }
    }
  };

  validateEmail = (email) => {
    const value = email || this.state.email;
    const voucherId = this.state.voucher.id;

    if (validateEmail({ value })) {
      const client = this.state.recipient;
      if (this.state.recipient && !this.state.originalEmail) {
        client.email = value;
        client.salon_id = this.props.currentSalon.id;
        this.props.saveClient(this.props.currentSalon.id, client);
      }
      this.setState({
        email: value,
        recipient: { ...client, email: value, voucherId },
      });
    }
  };

  showVoucherView = () => {
    this.setState({ dropdown: false, isNewDropdownOpen: false });
    this.props.feVoucherPanelToggle();
    this.props.feSidebarClose({ tag: 'SchedulerHeader.showVoucherView()' });
  };
  handleCloseVoucherView = () => void this.setState({ modalVoucher: true });

  handleCloseVoucherModal = () => {
    this.setState({ modalVoucher: false });
    this.props.feVoucherPanelToggle();
  };

  handleContinueVoucher = () => void this.setState({ modalVoucher: false });
  handleCloseSendEmailModal = () => void this.setState({ modalConfirmSendEmail: false });

  handleCloseVoucherSuccessModal = () => {
    this.props.feVoucherPanelToggle();
    this.props.clearCreatedVoucher();
  };

  handleCloseVoucherFailedModal = () => {
    this.setState({ modalVoucherFailed: false });
    this.props.feVoucherPanelToggle();
    this.props.clearCreatedVoucher();
  };

  openCheckout = () => {
    if (document.querySelector('.booking-steps-from-checkout') || document.querySelector('.BookingWindow.animated')) {
      return;
    }
    const { salonId } = this.props;
    this.props.setCheckoutInitDataFromNav(salonId);
    if (this.props.dayView) {
      this.props.history.push(`/salon/${salonId}/day/checkout`);
    } else if (this.props.history.location.pathname.includes('/week/')) {
      this.props.history.push(`/salon/${salonId}/week/${this.props.stylistId}/checkout`);
    }
  };

  handleAllStaffTodayClick = () => {
    const { selectedView, dayPick, displayStylists, salonId, history } = this.props;

    if (selectedView === DAY_VIEW) {
      dayPick(nowAsMoment());
    } else {
      const activeLink = displayStylists.length > 1 ? `/salon/${salonId}/day/today` : `/salon/${salonId}/day/active`;

      history.push(activeLink);
    }
  };

  handleResize = () => {
    this.setState({ windowWidth: window.innerWidth });
  };

  render() {
    const {
      modalConfirmSendEmail,
      recipient,
      errorMessage: errorMessageFromState,
      email: emailFromState,
      searchIsOpen,
      calendarModal: isPickerPopupRendered,
      searchContainer,
      modalVoucher,
      modalVoucherFailed,
      isNewDropdownOpen,
      windowWidth,
    } = this.state;

    const {
      show_only_active: showOnlyActive,
      onShowWeekView,
      selectedDate,
      dayView,
      setWeekView,
      selectedView,
      salonId,
      stylistId,
      defaultToWeekForOneStylist,
      dayPick,
      purchaseSmsBundle,
      permission,
      errorMessage: errorMessageFromProps,
      isFetching: fetching,
      sms_credits_available: smsCreditsAvailable,
      sms_credits_purchased_available: smsCreditsPurchasedAvailable,
      sms_credits_free_available: smsCreditsFreeAvailable,
      fetchSalon,
      history,
      trackEvent,
      createIfAllowed,
      setBookedByButton,
      children,
      voucherCreated,
      isDarkMode,
      isReceptionist,
      isVoucherPanelOpen,
      t,
      countryCode,
      currentSalon,
    } = this.props;

    const isMobile = windowWidth < MOBILE_VIEWPORT;
    const displayStylists = asArray(this.props.displayStylists);
    const activeStylistsWithColumn = filterActiveStylistsWithColumn(this.props.stylists);
    const isOneStylist = activeStylistsWithColumn.length === 1;
    const activeLink = isOneStylist ? `/salon/${salonId}/day/today` : `/salon/${salonId}/day/active`;
    const helpLink = HELP_LINKS[countryCode].baseHelpLink || HELP_LINKS.GB.baseHelpLink;

    // default to week view for one stylist salon
    if (dayView && isOneStylist && defaultToWeekForOneStylist) setWeekView(nowAsMoment(), activeStylistsWithColumn[0].id);

    const bgnMoment = getSchedulerWeekStartMonday(selectedDate);
    const endMoment = getSchedulerWeekEndSunday(selectedDate);

    if (USE_LOGGER_SCHEDAY) {
      const seg = formatSchedulerWeekUrlSegment(bgnMoment);
      const zBgn = fromAnyDateToZulu(bgnMoment);
      const zSel = fromAnyDateToZulu(selectedDate);
      const zEnd = fromAnyDateToZulu(endMoment);
      L_DAY.debug('SchedulerHeader.render()', `${seg} x ${zBgn} x ${zSel} x ${zEnd}`);
    }

    const currentWeekStartDate = getSchedulerWeekStartMonday(nowAsMoment());

    const isDayView = selectedView === DAY_VIEW;
    const isWeekView = selectedView === WEEK_VIEW;

    const isToday = isDayView && formatYearToDayCompact(selectedDate) === formatYearToDayCompact(nowAsMoment());

    const isCurrentWeek = isWeekView && formatYearToDayCompact(currentWeekStartDate) === formatYearToDayCompact(bgnMoment);

    const marketingLink = linkTo({ pattern: LINK.marketing, params: { salonId }, fault: '#' });
    const settingStylistLink = linkTo({ pattern: LINK.settingStylist, params: { salonId }, fault: '#' });

    const salonTimeZone = currentSalon?.timezone;

    const handleWeeklyPickerPopupClick = () => {
      trackEvent('click_date_picker_calendar');
      this.setState({ calendarModal: !isPickerPopupRendered });
    };

    const handleDailyPickerPopupClick = () => void this.setState({ calendarModal: !isPickerPopupRendered });

    // NOTE: the WORKAROUND_ID is used for document.queryElement() in Scheduler.rebook()
    //  and attached to renderedTodayButton of SchedulerHeader component
    const renderedTodayButton = (
      <Button tag="SchedulerHeader__today-button" variant={isToday ? 'primaryForce' : 'chameleon'} noMargin width="auto" onClick={() => dayPick(nowAsMoment())} data-workaround-id={WORKAROUND_ID}>
        <div data-bem="SchedulerHeader__today-button-text" className={CN.buttonTodayInnerSpace}>
          {t('scheduler.todayButton')}
        </div>
      </Button>
    );

    const renderedTodayButtonLink = (
      <Button tag="SchedulerHeader__today-button-link" variant={isToday ? 'primary' : 'chameleon'} noMargin width="auto" onClick={alwaysNoop}>
        <NavLink to={activeLink} className={CN.buttonLink}>
          <div className={CN.buttonInnerSpace}>{t('scheduler.todayButton')}</div>
        </NavLink>
      </Button>
    );

    const renderedThisWeekButton = (
      <Button tag="SchedulerHeader__this-week-button" variant={isCurrentWeek ? 'primaryForce' : 'chameleon'} noMargin onClick={() => dayPick(nowAsMoment())}>
        {t('scheduler.currentWeekButton')}
      </Button>
    );

    const renderedStylistButton = (
      <StylistDropdown
        {...this.props}
        stylists={activeStylistsWithColumn}
        dayView={dayView}
        displayStylists={displayStylists}
        hideInactive={this.hideInactiveHandler}
        showAllStylist={this.onShowAllStylist}
        onSelectStylist={this.onSelectStylist}
        onShowDayView={onShowWeekView}
        onClickAllStaffToday={this.handleAllStaffTodayClick}
        show_only_active={showOnlyActive}
        selectedStylistId={stylistId}
      />
    );

    const renderMarketingBlastContent = (
      <>
        <div
          className={cn({
            [CN.mobileCircleContainer]: isMobile,
          })}
        >
          <Icon variant="bubble" width="lg" />
        </div>
        <span className={CN.dropdownText}>{t('scheduler.marketingBlast')}</span>
        <ResponsiveBox.Mobile>
          <div className={CN.mobileArrow}>
            <Icon variant="arrowLeft" width="md" />
          </div>
        </ResponsiveBox.Mobile>
      </>
    );

    const marketingBlastClasses = cn({
      [CN.dropdownItem]: true,
      [CN.mobilePanelItem]: isMobile,
    });

    return (
      <div
        data-bem="SchedulerHeader"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
          'scheduler-header increase-z-index': searchIsOpen,
          'scheduler-header': !searchIsOpen,
        })}
      >
        <div data-bem="SchedulerHeader__top-header" className="top-header">
          <SidebarMenu />
          <div className="top-header-elm-container">
            <div className="combine-container-left">
              <SalonNameComponent />
              {USE_NAV_MARKETING_TEXT && (
                <ResponsiveBox.Desktop>
                  <MarketingTextHeader
                    bucketsPath={`/salon/${salonId}/communications/marketing`}
                    clientConnectPath={`/salon/${salonId}/communications/clientconnect`}
                    marketingPath={`/salon/${salonId}/communications/marketing`}
                    sms_credits_available={smsCreditsAvailable}
                    salonId={salonId}
                    purchaseSmsBundle={purchaseSmsBundle}
                    errorMessage={errorMessageFromProps}
                    isFetching={fetching}
                    permission={permission}
                    sms_credits_purchased_available={smsCreditsPurchasedAvailable}
                    sms_credits_free_available={smsCreditsFreeAvailable}
                    fetchSalon={fetchSalon}
                    history={history}
                  />
                </ResponsiveBox.Desktop>
              )}
            </div>

            <div className="combine-container-right">
              {USE_NAV_ICONS && (
                <>
                  <ResponsiveBox.Desktop>
                    <div
                      className={cn({
                        helpIconContainer: true,
                        weekView: isWeekView,
                      })}
                    >
                      <ExternalLink link={helpLink} variant="inline">
                        <div className="helpIcon">{t('common.buttons.help')}</div>
                      </ExternalLink>
                    </div>
                  </ResponsiveBox.Desktop>
                </>
              )}
              <SmsBuyButton tag="SchedulerHeader__SmsBuyButton" />
              <CogButton tag="SchedulerHeader__CogButton" />
              <ActivityFeedButton history={history} salonId={salonId} />
              <PadLock inScheduler />
              <GlossaryIcons tag="SchedulerHeader__GlossaryIcons" />
            </div>
          </div>
        </div>
        <div data-bem="SchedulerHeader__sub-header" className={CN.subHeader}>
          <section className={isWeekView ? 'W-view' : 'D-view'}>
            <div className={CN.headerControls}>
              <div data-bem="SchedulerHeader__left-buttons" className={CN.flex}>
                {isWeekView ? (
                  <div data-bem="SchedulerHeader__week-view-one-stylist">
                    {isOneStylist ? (
                      <div className={CN.controlsOnLeft}>
                        <div className={CN.buttonGapRight}>
                          <ResponsiveBox.Desktop>
                            <div className={CN.flex}>
                              {renderedThisWeekButton}
                              <div className={CN.buttonGap}>{renderedTodayButtonLink}</div>
                              {renderedStylistButton}
                            </div>
                          </ResponsiveBox.Desktop>
                          <ResponsiveBox.Mobile>{renderedStylistButton}</ResponsiveBox.Mobile>
                        </div>
                      </div>
                    ) : (
                      <div data-bem="SchedulerHeader__week-view-multiple-stylists" className={CN.flex}>
                        <ResponsiveBox.Mobile>
                          <ButtonMobileStaff />
                        </ResponsiveBox.Mobile>
                        <ResponsiveBox.Desktop>
                          <div className={CN.controlsOnLeft}>
                            {renderedThisWeekButton}
                            <div className={CN.buttonGap}>{renderedTodayButtonLink}</div> {renderedStylistButton}
                          </div>
                        </ResponsiveBox.Desktop>
                      </div>
                    )}
                  </div>
                ) : (
                  <div data-bem="SchedulerHeader__day-view-one-stylist">
                    {isOneStylist ? (
                      <div className={CN.controlsOnLeft}>
                        <ResponsiveBox.Desktop>
                          <div className={CN.buttonGapRight}>{renderedTodayButton}</div>
                        </ResponsiveBox.Desktop>
                        {renderedStylistButton}
                      </div>
                    ) : (
                      <div data-bem="SchedulerHeader__day-view-multiple-stylists">
                        <ResponsiveBox.Mobile>
                          <ButtonMobileStaff />
                        </ResponsiveBox.Mobile>
                        <ResponsiveBox.Desktop>
                          <div data-bem="SchedulerHeader__left-buttons-day-view" className={CN.controlsOnLeft}>
                            <div className={CN.buttonGapRight}>{renderedTodayButton}</div>
                            {renderedStylistButton}
                          </div>
                        </ResponsiveBox.Desktop>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div data-bem="SchedulerHeader__right-buttons" className={CN.flex}>
                <ResponsiveBox.Desktop>
                  {/*  Print button  */}
                  {children || null}
                </ResponsiveBox.Desktop>

                <div className={CN.buttonGap}>{searchContainer ? <ClientSearch {...this.props} setSearchOpen={this.setSearchOpen} /> : null}</div>
                <div data-bem="SchedulerHeader_dropdown-new" ref={this.dropdownRef} className={CN.dropdownNew}>
                  <Button tag="SchedulerHeader__button-new" variant="chameleon" noMargin width="medium" onClick={this.toggleNewDropdown} active={isNewDropdownOpen}>
                    <div className={CN.buttonInnerSpace}>
                      <span className={CN.space}>
                        <Icon variant="plus" width="lg" />
                      </span>
                      {t('common.buttons.newUpp')}
                    </div>
                  </Button>
                  {isNewDropdownOpen && (
                    <DropdownFoldout
                      width={isMobile ? 'full' : 'sm'}
                      position={isMobile ? 'full' : 'bottomRight'}
                      variant={isMobile && 'panel'}
                      onClickOutside={(e) => {
                        if (!e || !e.target) return;

                        if (this.dropdownRef.current?.contains(e.target)) {
                          return;
                        }

                        this.setState({ isStylistOpen: false });
                      }}
                    >
                      <DropdownFoldout.Body>
                        <ResponsiveBox.Mobile>
                          <div data-bem="SchedulerHeader__button-close-mobile-panel" className={CN.buttonCloseMobileNewPanel} onClick={this.toggleNewDropdown}>
                            <Icon variant="closeDark" width="lg" />
                          </div>
                        </ResponsiveBox.Mobile>

                        <div data-bem="SchedulerHeader__items" className={CN.dropdownItems}>
                          <ResponsiveBox.Mobile>
                            <div className={CN.titleMobilePanel}>{t('scheduler.addNewTitle')}</div>
                          </ResponsiveBox.Mobile>

                          {isDayView && (
                            <SchedulerHeaderGatedAddAppointmentItem
                              t={t}
                              isMobile={isMobile}
                              salonId={salonId}
                              selectedDate={selectedDate}
                              createIfAllowed={createIfAllowed}
                              setBookedByButton={setBookedByButton}
                              trackEvent={trackEvent}
                              onRequestClose={() => this.setState({ dropdown: false, isNewDropdownOpen: false })}
                              searchIsOpen={searchIsOpen}
                            />
                          )}

                          <Button tag="SchedulerHeader__item-sale" variant={isMobile ? 'mobilePanelItem' : 'dropdownItem'} height={isMobile ? 'lg2' : 'max'} onClick={this.openCheckout}>
                            <div
                              className={cn({
                                [CN.mobileCircleContainer]: isMobile,
                              })}
                            >
                              <Icon variant="shoppingBag" width="lg" unselected />
                            </div>
                            <span className={CN.dropdownText}>{t('scheduler.retailSale')}</span>
                            <ResponsiveBox.Mobile>
                              <div className={CN.mobileArrow}>
                                <Icon variant="arrowLeft" width="md" />
                              </div>
                            </ResponsiveBox.Mobile>
                          </Button>
                          <Button tag="SchedulerHeader__item-voucher" variant={isMobile ? 'mobilePanelItem' : 'dropdownItem'} height={isMobile ? 'lg2' : 'max'} onClick={this.showVoucherView}>
                            <div
                              className={cn({
                                [CN.mobileCircleContainer]: isMobile,
                              })}
                            >
                              <Icon variant="gift" width="lg" unselected />
                            </div>
                            <span className={CN.dropdownText}>{t('scheduler.voucherSale')}</span>

                            <ResponsiveBox.Mobile>
                              <div className={CN.mobileArrow}>
                                <Icon variant="arrowLeft" width="md" />
                              </div>
                            </ResponsiveBox.Mobile>
                          </Button>

                          {isReceptionist ? (
                            <Tooltip variant="slickTop" content={<TooltipContentReceptionist />}>
                              <div className={marketingBlastClasses}>{renderMarketingBlastContent}</div>
                            </Tooltip>
                          ) : (
                            <NavLink to={marketingLink} className={marketingBlastClasses}>
                              {renderMarketingBlastContent}
                            </NavLink>
                          )}

                          <div data-bem="SchedulerHeader__item-user">
                            <NavLink
                              to={settingStylistLink}
                              className={cn({
                                [CN.dropdownItem]: true,
                                [CN.mobilePanelItem]: isMobile,
                              })}
                            >
                              <div
                                className={cn({
                                  [CN.mobileCircleContainer]: isMobile,
                                })}
                              >
                                <Icon variant="user" width="lg" />
                              </div>
                              <span className={CN.dropdownText}>{t('scheduler.teamMember')}</span>
                              <ResponsiveBox.Mobile>
                                <div className={CN.mobileArrow}>
                                  <Icon variant="arrowLeft" width="md" />
                                </div>
                              </ResponsiveBox.Mobile>
                            </NavLink>
                          </div>
                        </div>
                      </DropdownFoldout.Body>
                    </DropdownFoldout>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>

        <ResponsiveBox.Mobile>
          <div data-bem="SchedulerHeader__mobile-footer" className={CN.footerControlsMobile}>
            {isWeekView ? (
              <div data-bem="SchedulerHeader__mobile-footer-week-view">{isOneStylist ? <ResponsiveBox.Mobile>{renderedThisWeekButton}</ResponsiveBox.Mobile> : renderedThisWeekButton}</div>
            ) : (
              <div data-bem="SchedulerHeader__mobile-footer-day-view">{isOneStylist ? <ResponsiveBox.Mobile>{renderedTodayButton}</ResponsiveBox.Mobile> : renderedTodayButton}</div>
            )}
          </div>
        </ResponsiveBox.Mobile>

        {isVoucherPanelOpen && <VoucherView onCloseVoucherView={this.handleCloseVoucherView} />}

        <SalonModal tag="SchedulerHeader__modal--voucher-exit" isOpen={modalVoucher} withFooter>
          <SalonModal.Body>
            <IconStatus variant="alert">
              <Icon variant="alert" />
            </IconStatus>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('scheduler.exitTitle')}</div>
              <span className={CN.modalText}>{t('scheduler.exitDescription')}</span>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <Button variant="outlineForce" onClick={this.handleContinueVoucher} xSpace>
                {t('scheduler.cancelExit')}
              </Button>
              <Button variant="primaryForce" onClick={() => void this.handleCloseVoucherModal()} xSpace>
                {t('scheduler.confirmExit')}
              </Button>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        {fromAnyToBool(modalConfirmSendEmail) && (
          <SalonModal tag="SchedulerHeader__modal--confirm" isOpen variant="noFooter">
            <SalonModal.Header>
              <div className={CN.closeButtonContainer}>
                <Button variant="empty" onClick={this.handleCloseSendEmailModal}>
                  <Icon variant="close" variantInvert />
                </Button>
              </div>
            </SalonModal.Header>
            <SalonModal.Body>
              <div className={CN.modalImage}>
                <div className={CN.circleConfirm}>
                  <Icon variant="handUp" />
                </div>
              </div>
              <div className={CN.modalContent}>
                <div className={CN.title}>
                  {t('scheduler.voucherSentTo')}
                  <div className={CN.recipientName}>{recipient && recipient.email ? recipient.email : emailFromState}</div>
                </div>
              </div>
            </SalonModal.Body>
          </SalonModal>
        )}

        {voucherCreated?.id && (
          <SalonModal tag="SchedulerHeader__modal--success" isOpen>
            <SalonModal.Header>
              <div className={CN.closeButtonContainer}>
                <Button variant="empty" onClick={this.handleCloseVoucherSuccessModal}>
                  <Icon variant="close" variantInvert />
                </Button>
              </div>
            </SalonModal.Header>
            <SalonModal.Body>
              <div className={CN.modalImage}>
                <div className={CN.circle}>
                  <Icon variant="confirm" width="lg" />
                </div>
              </div>
              <div className={CN.modalContent}>
                <div className={CN.title}>{t('scheduler.voucherCreatedTitle')}</div>
                <div className={CN.subTitle}>{t('scheduler.voucherCreatedSubtitle')}</div>

                <div className={CN.inputContainer}>
                  <div className={CN.inputLabel}>{t('scheduler.enterEmailAddress')}</div>
                  <input
                    id="email"
                    className="input-default"
                    type="text"
                    onChange={(e) =>
                      void this.setState({
                        email: e.target.value,
                        recipient: { ...recipient, email: e.target.value },
                      })
                    }
                    onBlur={(e) => void this.validateEmail(e.target.value)}
                    value={recipient && recipient.email ? recipient.email : emailFromState || ''}
                    placeholder=""
                    autoComplete="off"
                  />
                  <div className={CN.error}>{errorMessageFromState}</div>
                </div>
                <div className={CN.note}>{t('scheduler.note')}</div>
              </div>
            </SalonModal.Body>

            <SalonModal.Footer>
              <Button variant="primaryForce" onClick={this.emailVoucherPurchaser} disabled={!validateEmail({ value: emailFromState })} xSpace>
                {t('scheduler.sendVoucher')}
              </Button>
            </SalonModal.Footer>
          </SalonModal>
        )}

        <SalonModal tag="ProcedureModal__modal--payments" isOpen={modalVoucherFailed} withFooter>
          <SalonModal.Body>
            <IconStatus variant="alert">
              <Icon variant="alert" />
            </IconStatus>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('scheduler.voucherErrorTitle')}</div>
              <span className={CN.modalText}>{t('scheduler.voucherErrorDescription')}</span>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <Button variant="primaryForce" onClick={() => void this.handleCloseVoucherFailedModal()} xSpace>
                {t('common.buttons.ok')}
              </Button>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <div data-bem="SchedulerHeader__date-picker-container" className={CN.pickerContainer}>
          <div
            data-bem="SchedulerHeader__date-picker-popup"
            className={cn({
              [CN.pickerButton]: true,
              [CN.pickerButtonWeeView]: isWeekView,
              'range-dpicker-input': true,
              'range-dpicker-input isOpen': isPickerPopupRendered,
            })}
          >
            <div data-bem="SchedulerHeader__date-next" data-pw-id="scheduler:nav:next" className="calendar-arrow prev" role="button" tabIndex={0} onClick={this.calendarNextClicked} />

            {isDayView && <DayViewDisplayDate selectedDate={selectedDate} onPickerOpen={handleDailyPickerPopupClick} />}

            {isWeekView && (
              <div onKeyDown={alwaysNoop} tabIndex="0" className={CN.pickerButtonText} style={{ cursor: 'pointer' }} role="button" onClick={handleWeeklyPickerPopupClick}>
                <WeekViewIntervalDisplay bgn={bgnMoment} end={endMoment} />
              </div>
            )}

            <div data-bem="SchedulerHeader__date-prev" data-pw-id="scheduler:nav:prev" className="calendar-arrow next" role="button" tabIndex={0} onClick={this.calendarPrevClicked} />

            {isPickerPopupRendered && (
              <div>
                <div className={isPickerPopupRendered ? 'dpicker-modal cfix' : 'dpicker-modal cfix isOpen'}>
                  {/*  https://github.com/Adphorus/react-date-range */}

                  {isDayView && (
                    <div className="co-date-picker">
                      <div className={CN.mobileClosePickerButton}>
                        <Button variant="icon" width="small" onClick={handleDailyPickerPopupClick}>
                          <Icon variant="closeDark" width="lg" />
                        </Button>
                      </div>
                      <CustomDatePicker
                        className={CN.schedulerPicker}
                        key={selectedDate.format('YYYYMMDD')}
                        weekStartDay={1}
                        selectedDate={momentToSalonCalendarDate(selectedDate, salonTimeZone)}
                        inline
                        onChange={this.handleSchedulerDayPicked}
                        onOutsideClick={alwaysNoop}
                      />
                    </div>
                  )}
                  {isWeekView && (
                    <div data-bem="SchedulerHeader__week-CustomDatePicker" className={CN.weekPickerContainer}>
                      <div className={CN.mobileClosePickerButton}>
                        <Button variant="icon" width="small" onClick={handleDailyPickerPopupClick}>
                          <Icon variant="closeDark" width="lg" />
                        </Button>
                      </div>
                      <CustomDatePicker
                        className={CN.schedulerPicker}
                        key={formatSchedulerWeekUrlSegment(selectedDate)}
                        weekStartDay={1}
                        selectedDate={momentToSalonCalendarDate(selectedDate, salonTimeZone)}
                        highlightRangeStart={momentToSalonCalendarDate(bgnMoment, salonTimeZone)}
                        highlightRangeEnd={momentToSalonCalendarDate(endMoment, salonTimeZone)}
                        onChange={this.handleSchedulerWeekPicked}
                        onOutsideClick={alwaysNoop}
                        inline
                      />
                    </div>
                  )}
                  <DatePickerInput tag="SchedulerHeader__week-component" component={DatePickerInput.WeeksComponent} onChangeDate={this.weekPick} selectedDate={selectedDate} />
                </div>
                <div role="button" tabIndex={0} onKeyDown={alwaysNoop} className={isPickerPopupRendered ? 'outside-overlay' : ''} onClick={handleDailyPickerPopupClick} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const SchedulerHeaderHoc = connect(
  (state) => ({
    currentSalon: selectCurrentSalon(state),
    isReceptionist: isEffectiveReceptionist(state),
    emailSuccess: state?.settings?.balanceSheet?.emailSuccess,
    bookedByButton: state?.scheduler?.bookedByButton,
    waiting_procedures: state?.scheduler?.waiting_procedures,
    waiting_events: state?.scheduler?.waiting_events,
    isFetching: state?.clientCommunications?.isFetching,
    sms_credits_available: state?.clientCommunications?.sms_credits_available,
    voucherCreated: state?.cashup?.voucherCreated,
    voucherList: state?.cashup?.voucherCreated,
    sms_credits_free_available: state?.currentSalon?.sms_credits_free_available,
    sms_credits_purchased_available: state?.currentSalon?.sms_credits_purchased_available,
    errorMessage: state?.clientCommunications?.errorMessage,
    isVoucherPanelOpen: selectVoucherPanelOpen(state),
    countryCode: selectCurrentSalonCountryCode(state),
  }),
  mapActionsToDispatchToProps({
    trackEvent,
    createIfAllowed,
    setBookedByButton,
    setCheckoutInitDataFromNav,
    clearCreatedVoucher,
    saveClient,
    fetchSalon,
    beSendVoucherEmail,
    feVoucherPanelToggle,
    feSidebarClose,
  }),
)(withTheme()(withTranslation()(SchedulerHeader)));

export default SchedulerHeaderHoc;
